import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { 
  BookOpen, Plus, Pencil, Trash2, Loader2, 
  Building2, GitBranch, Database, Filter, Calculator, Info
} from 'lucide-react';
import { toast } from 'sonner';
import { getBusinessRules, saveBusinessRule, updateBusinessRule, deleteBusinessRule } from '../../services/api';

const RULE_CATEGORIES = [
  { value: 'hierarchy', label: 'Hierarchy/Structure', icon: GitBranch, description: 'Parent-child relationships (e.g., Brand → Agency)' },
  { value: 'filtering', label: 'Filtering Rules', icon: Filter, description: 'How to filter data (e.g., NULL means direct)' },
  { value: 'aggregation', label: 'Aggregation Rules', icon: Calculator, description: 'Pre-aggregated tables, GROUP BY rules' },
  { value: 'naming', label: 'Naming Conventions', icon: Database, description: 'Column naming patterns, ID conventions' },
  { value: 'general', label: 'General', icon: BookOpen, description: 'Other business rules' },
];

const BusinessRules = ({ user }) => {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  
  const [form, setForm] = useState({
    name: '',
    rule: '',
    category: 'general',
    examples: ''
  });

  useEffect(() => {
    fetchRules();
  }, []);

  const fetchRules = async () => {
    try {
      setLoading(true);
      const data = await getBusinessRules();
      setRules(data.rules || []);
    } catch (error) {
      toast.error('Failed to load business rules');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.rule.trim()) {
      toast.error('Name and rule description are required');
      return;
    }

    setSaving(true);
    try {
      const examples = form.examples.split('\n').map(e => e.trim()).filter(Boolean);
      
      if (editingRule) {
        await updateBusinessRule(editingRule.id, form.name, form.rule, form.category, examples);
        toast.success('Business rule updated');
      } else {
        await saveBusinessRule(form.name, form.rule, form.category, examples);
        toast.success('Business rule added - AI will now follow this rule!');
      }
      
      setDialogOpen(false);
      resetForm();
      fetchRules();
    } catch (error) {
      toast.error('Failed to save business rule');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (rule) => {
    setEditingRule(rule);
    setForm({
      name: rule.name || '',
      rule: rule.rule || '',
      category: rule.category || 'general',
      examples: (rule.examples || []).join('\n')
    });
    setDialogOpen(true);
  };

  const handleDelete = async (ruleId) => {
    if (!window.confirm('Delete this business rule?')) return;
    
    try {
      await deleteBusinessRule(ruleId);
      toast.success('Business rule deleted');
      fetchRules();
    } catch (error) {
      toast.error('Failed to delete rule');
    }
  };

  const resetForm = () => {
    setForm({ name: '', rule: '', category: 'general', examples: '' });
    setEditingRule(null);
  };

  const getCategoryIcon = (category) => {
    const cat = RULE_CATEGORIES.find(c => c.value === category);
    return cat ? cat.icon : BookOpen;
  };

  const getCategoryLabel = (category) => {
    const cat = RULE_CATEGORIES.find(c => c.value === category);
    return cat ? cat.label : 'General';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Building2 className="w-5 h-5 text-purple-500" />
            Business Rules & Domain Knowledge
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            Define rules that AI must follow when generating queries. These rules appear in every AI prompt.
          </p>
        </div>
        {user?.role === 'admin' && (
          <Button onClick={() => { resetForm(); setDialogOpen(true); }} className="bg-purple-500 hover:bg-purple-600">
            <Plus className="w-4 h-4 mr-2" />
            Add Rule
          </Button>
        )}
      </div>

      {/* Info Box */}
      <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-purple-500 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-purple-800">How Business Rules Work</p>
            <p className="text-sm text-purple-700 mt-1">
              Rules you define here are shown to the AI with every query under <strong>"BUSINESS RULES & DOMAIN KNOWLEDGE (MUST FOLLOW)"</strong>. 
              Use this to teach AI about:
            </p>
            <ul className="text-sm text-purple-700 mt-2 space-y-1 list-disc list-inside">
              <li><strong>Hierarchies:</strong> Brand → Agency relationships, Parent → Child structures</li>
              <li><strong>Filtering:</strong> What NULL means, how to filter by different levels</li>
              <li><strong>Aggregation:</strong> Which tables are pre-aggregated, when NOT to use GROUP BY</li>
              <li><strong>Naming:</strong> Column naming conventions specific to your database</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Rules List */}
      {rules.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-gray-500">
            <BookOpen className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>No business rules defined yet</p>
            <p className="text-sm mt-1">Add rules to teach AI about your domain</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {rules.map((rule) => {
            const Icon = getCategoryIcon(rule.category);
            return (
              <Card key={rule.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Icon className="w-4 h-4 text-purple-500" />
                        <h4 className="font-medium text-gray-900">{rule.name}</h4>
                        <Badge variant="secondary" className="text-xs">
                          {getCategoryLabel(rule.category)}
                        </Badge>
                      </div>
                      
                      <p className="text-sm text-gray-700 whitespace-pre-wrap">{rule.rule}</p>
                      
                      {rule.examples?.length > 0 && (
                        <div className="mt-3 p-2 bg-gray-50 rounded">
                          <p className="text-xs font-medium text-gray-500 mb-1">Examples:</p>
                          <ul className="text-xs text-gray-600 space-y-1">
                            {rule.examples.map((ex, idx) => (
                              <li key={idx}>• {ex}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      <p className="text-xs text-gray-400 mt-2">
                        Added by {rule.created_by || 'Admin'}
                      </p>
                    </div>
                    
                    {user?.role === 'admin' && (
                      <div className="flex gap-1">
                        <Button variant="outline" size="sm" onClick={() => handleEdit(rule)}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={() => handleDelete(rule.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-purple-500" />
              {editingRule ? 'Edit Business Rule' : 'Add Business Rule'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Rule Name *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g., Brand and Agency Hierarchy"
              />
            </div>
            
            <div>
              <Label>Category</Label>
              <Select value={form.category} onValueChange={(val) => setForm({ ...form, category: val })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {RULE_CATEGORIES.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value}>
                      <div className="flex items-center gap-2">
                        <cat.icon className="w-4 h-4" />
                        <span>{cat.label}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                {RULE_CATEGORIES.find(c => c.value === form.category)?.description}
              </p>
            </div>
            
            <div>
              <Label>Rule Description * <span className="text-xs text-gray-500">(Be specific - AI reads this exactly)</span></Label>
              <Textarea
                value={form.rule}
                onChange={(e) => setForm({ ...form, rule: e.target.value })}
                placeholder="e.g., In our platform, brand_id (e.g., 31) is the PARENT. agent_user_id (e.g., 3) is an AGENCY under that brand. Multiple agencies can exist under a single brand. When filtering: brand_id=31 gets ALL data for that brand. Adding agent_user_id=3 filters to a specific agency. When agent_user_id IS NULL, it means direct brand data (not through an agency)."
                className="min-h-[120px]"
              />
            </div>
            
            <div>
              <Label>Examples <span className="text-xs text-gray-500">(one per line, optional)</span></Label>
              <Textarea
                value={form.examples}
                onChange={(e) => setForm({ ...form, examples: e.target.value })}
                placeholder="brand_id=31 AND agent_user_id IS NULL = Direct brand data
brand_id=31 AND agent_user_id=3 = Specific agency under brand 31"
                className="min-h-[80px] font-mono text-sm"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} className="bg-purple-500 hover:bg-purple-600">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editingRule ? 'Update Rule' : 'Add Rule'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BusinessRules;
